import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";

import AddIcon from "../../../../components/icon/icons/layer-22-icon.component";
import { AddProductModal } from "../../../../features/products/features/add-product-modal/components/add-product-modal/add-product-modal.component";
import { ProductsTable } from "../../../../features/products/components/products-table/products-table.component";
import usePermissions from "../../../../hooks/usePermissions";
import { isAddProductModalOpenWO } from "../../../../redux/selectors/workOrders";
import {
  hideWorkOrderAddProductModal,
  showWorkOrderAddProductModal,
} from "../../../../redux/actions/workOrderActions";
import { OutlinedButton } from "../../../../components/form-controls/button/outlined-button/outlined-button.component";
import { canDownloadProductsDetails } from "../../../../types/product/ibrief-product/ibrief-product.functions";
import { FileDownload } from "../../../../features/file-attachments/file-download/components/file-download/file-download-component";
import { IProductDetail } from "../../../../types/product/iproduct-detail.interface";
import { PullRight } from "../../../../components/layout/pull-right/pull-right.styles";
import { ProductHistory } from "../../../../features/product-history/components/product-history.component";
import { BasicResourceType } from "../../../../types/resources/resources.enums";
import { createFormRef } from "../../../../features/forms/types/forms.functions";
import { GreenButton } from "../../../../components/form-controls/button/green-button/green-button.component";
import { BriefProductsForm } from "../../../../features/products/types/brief-product-form/brief-product-form.interface";
import { SpinnerLoader } from "../../../../components/loaders";
import { ErrorBox } from "../../../../components/error-box/error-box.component";
import { dateIsBeforeToday } from "../../../../utils/date-format.utils";

import { workOrderProductsFileDownloadEndPoint } from "../../features/work-order-products/services/work-order-products.service";
import * as workOrderProductsStore from "../../features/work-order-products/store/work-order-products.store";
import { productsValidation } from "../../types/work-order-products/work-order-products.validation";

import { useWorkOrderProductsTabStyles } from "./work-order-products-tabs.styles";
import { WorkOrderProductTabProps } from "./work-order-products-tab.props";
import { HiddenHeading } from "../../../pages.styles";

export const WorkOrderProductsTab = (props: WorkOrderProductTabProps) => {
  const dispatch = useDispatch();
  const userPermissions = usePermissions();
  const formRef = useRef<any>(createFormRef());
  const [formErrors, setFormErrors] = useState({});

  const errors = useSelector(workOrderProductsStore.getErrors);
  const products = useSelector(workOrderProductsStore.getProducts);
  const isLoading = useSelector(workOrderProductsStore.isLoading);
  const classes = useWorkOrderProductsTabStyles();

  useEffect(() => {
    workOrderProductsStore.fetchProducts(props.workOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openProductModal = () => {
    dispatch(showWorkOrderAddProductModal());
  };

  const onAddProduct = (product: IProductDetail) =>
    workOrderProductsStore.addProduct(product.id, props.workOrderId);

  const onCloseProductModal = () => dispatch(hideWorkOrderAddProductModal());

  const onSubmit = (form: BriefProductsForm) => {
    workOrderProductsStore.updateProducts(form.products, props.workOrderId);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <HiddenHeading>{props.pageTitle}</HiddenHeading>
        <PullRight>
          <SpinnerLoader showIf={isLoading} inline size={25} />
          <GreenButton
            disabled={isLoading}
            marginAround
            onClick={() => formRef.current.handleSubmit(onSubmit)()}
          >
            Save
          </GreenButton>
          <ProductHistory
            showIf={!!products.length}
            buttonText="Show History"
            buttonSize="small"
            className={classes.marginRight}
            customComponent={OutlinedButton}
            resourceId={props.workOrderId}
            resourceType={BasicResourceType.WorkOrder}
          />
          <FileDownload
            icon={<GetAppIcon />}
            authRequired={true}
            url={workOrderProductsFileDownloadEndPoint(props.workOrderId)}
            showIf={canDownloadProductsDetails(products, userPermissions)}
            saveAsFileName="work-order-products.csv"
            variant="button"
          >
            Export CSV
          </FileDownload>
        </PullRight>
        <ErrorBox
          fullHeight={false}
          message={errors?.errorMessage!}
          showIf={useSelector(workOrderProductsStore.hasErrors)}
        />
        <ErrorBox
          fullHeight={false}
          message={"Ensure proper Buy and Get relationships are defined"}
          showIf={!!Object.values(formErrors).length}
        />
        <ProductsTable
          showIf={!!products.length}
          briefEndDatePassed={dateIsBeforeToday(props.endDate ?? "")}
          formRef={formRef}
          pageType="work-orders"
          products={products}
          productsValidation={productsValidation}
          resourceType={BasicResourceType.WorkOrderProducts}
          setFormErrors={setFormErrors}
          setProductsFormIsDirty={props.setProductsFormIsDirty}
        />
        <OutlinedButton
          disabled={!userPermissions.manageProducts}
          onClick={openProductModal}
          variant={"outlined"}
          startIcon={<AddIcon />}
        >
          Add Product
        </OutlinedButton>
      </Grid>
      <AddProductModal
        pageType="work-orders"
        isOpen={useSelector(isAddProductModalOpenWO)}
        onClose={onCloseProductModal}
        products={products}
        onAddProduct={onAddProduct}
      />
    </Grid>
  );
};
